import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import Label from '../../../components/Label';
import { fDateTime } from '../../../utils/formatTime';
import MetricCard from './MetricCard';
import { EmptyModuleState, LoadingPanel, SectionError } from './ManageUserDialogStates';
import { fetchAdmin, postAdmin } from '../../../admin/api';

function formatText(value) {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }
  return `${value}`;
}

function formatDateTime(value) {
  if (!value) return 'N/A';
  try {
    return fDateTime(value);
  } catch {
    return formatText(value);
  }
}

function AccountBadge({ bankName, bankId, isPrimary }) {
  const initials = (bankName || bankId || 'BN').slice(0, 2).toUpperCase();

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Avatar sx={{ width: 34, height: 34, fontSize: 13 }}>
        {initials}
      </Avatar>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
            {formatText(bankName)}
          </Typography>
          {isPrimary ? <Chip size="small" color="primary" label="Primary" /> : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Bank ID: {formatText(bankId)}
        </Typography>
      </Box>
    </Stack>
  );
}

AccountBadge.propTypes = {
  bankName: PropTypes.string,
  bankId: PropTypes.string,
  isPrimary: PropTypes.bool,
};

function DetailField({ label, value, monospace = false }) {
  return (
    <Stack spacing={0.4}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: monospace ? 'monospace' : 'inherit',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  monospace: PropTypes.bool,
};

function ReviewResultDialog({ open, onClose, loading, data, evaluationError }) {
  const statusColor =
    data?.review_status === 'approved'
      ? 'success'
      : data?.review_status === 'rejected'
        ? 'error'
        : 'warning';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6">Account Review</Typography>
            <Typography variant="body2" color="text.secondary">
              Evaluation report for the selected saved bank account.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ minHeight: 360, position: 'relative', p: 0 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading account review...
            </Typography>
          </Stack>
        ) : evaluationError ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="error">
              {evaluationError}
            </Typography>
          </Box>
        ) : data ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Review status
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
                      <Label color={statusColor}>{formatText(data.review_status)}</Label>
                      <Chip size="small" label={`Match score: ${formatText(data.match_score)}`} />
                    </Stack>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">
                      Decision code
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                      {formatText(data.match_status)}
                    </Typography>
                  </Box>
                </Stack>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Account Details
                    </Typography>
                    <Stack spacing={2}>
                      <DetailField label="Account name" value={formatText(data.account_name)} />
                      <DetailField label="Account number" value={formatText(data.account_number)} monospace />
                      <DetailField label="Bank account ID" value={formatText(data.bank_account_id)} monospace />
                      <DetailField label="User ID" value={formatText(data.user_id)} monospace />
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      KYC / Review Context
                    </Typography>
                    <Stack spacing={2}>
                      <DetailField label="KYC name" value={formatText(data.kyc_name)} />
                      <DetailField label="BVN name" value={formatText(data.bvn_name)} />
                      <DetailField label="Allowed" value={data.allowed ? 'Yes' : 'No'} />
                      <DetailField label="Requires review" value={data.requires_review ? 'Yes' : 'No'} />
                      <DetailField label="Reviewed by" value={formatText(data.reviewed_by)} monospace />
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Audit Timeline
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <DetailField label="Created at" value={formatDateTime(data.created_at)} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField label="Reviewed at" value={formatDateTime(data.reviewed_at)} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField label="Updated at" value={formatDateTime(data.updated_at)} />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Review Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatText(data.review_notes)}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

ReviewResultDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  data: PropTypes.object,
  evaluationError: PropTypes.string,
};

export default function SavedBankAccount({ userId }) {
  const [state, setState] = useState({
    loading: false,
    loaded: false,
    error: null,
    rows: [],
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewError, setReviewError] = useState(null);

  const loadSavedAccounts = useCallback(async () => {
    if (!userId) return;

    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const response = await fetchAdmin(`/users/${userId}/saved_banks`);
      const rows = Array.isArray(response) ? response : response?.data || [];

      setState({
        loading: false,
        loaded: true,
        error: null,
        rows,
      });

      setSelectedRow((current) =>
        current && rows.some((r) => String(r.id) === String(current.id)) ? current : rows[0] || null
      );
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        loaded: true,
        error: error?.message || 'Failed to load saved bank accounts.',
      }));
    }
  }, [userId]);

  useEffect(() => {
    loadSavedAccounts();
  }, [loadSavedAccounts, refreshKey]);

  const rows = useMemo(() => state.rows || [], [state.rows]);

  const pagedRows = useMemo(() => {
    const start = page * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const total = rows.length;
  const primaryCount = useMemo(() => rows.filter((row) => Number(row.is_primary) === 1).length, [rows]);
  const activeCount = useMemo(() => rows.filter((row) => row.status === 'active').length, [rows]);

  const handleEvaluate = async (row) => {
    setSelectedRow(row);
    setReviewOpen(true);
    setReviewLoading(true);
    setReviewError(null);
    setReviewData(null);

    try {
      const response = await postAdmin(`/users/${userId}/primary_account/review`, {
        bank_account_id: row.id,
      });

      const evaluation = response?.evaluation || response?.data?.evaluation || null;

      setReviewData({
        id: row.id,
        user_id: userId,
        bank_account_id: row.id,
        account_number: row.account_number,
        account_name: row.account_name,
        kyc_name: evaluation?.kyc_name || row.account_name,
        match_score: evaluation?.match_score ?? 0,
        match_status: evaluation?.decision_code || evaluation?.match_status || 'N/A',
        review_status: evaluation?.requires_review ? 'rejected' : 'approved',
        review_notes: evaluation?.requires_review ? 'Needs to be reviewed' : 'No review required',
        reviewed_by: 'system',
        reviewed_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        created_at: row.created_at,
        updated_at: row.updated_at,
        allowed: Boolean(evaluation?.allowed),
        requires_review: Boolean(evaluation?.requires_review),
        matched_rules: evaluation?.matched_rules || [],
      });
    } catch (error) {
      setReviewError(error?.message || 'Failed to evaluate account.');
    } finally {
      setReviewLoading(false);
    }
  };

  if (!userId) {
    return (
      <EmptyModuleState
        title="No user selected"
        description="Load a user profile to inspect saved bank accounts."
        icon="solar:user-id-bold"
      />
    );
  }

  if (state.loading && !state.loaded) {
    return <LoadingPanel />;
  }

  if (state.error) {
    return (
      <SectionError
        message={state.error}
        onRetry={() => {
          setRefreshKey((value) => value + 1);
        }}
      />
    );
  }

  return (
    <Stack spacing={3}>

      <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 3, py: 2 }}>
          <Box>
            <Typography variant="h6">Saved Bank Accounts</Typography>
            <Typography variant="body2" color="text.secondary">
              Admin audit list of user-owned bank accounts and primary account checks.
            </Typography>
          </Box>
          <Chip label={`${total} records`} variant="outlined" />
        </Stack>

        <Divider />

        {rows.length ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Primary</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row) => {
                    const isSelected = String(selectedRow?.id) === String(row.id);
                    const isPrimary = Number(row.is_primary) === 1;

                    return (
                      <TableRow
                        key={row.id}
                        // hover
                        // selected={isSelected}
                        // onClick={() => {
                        //   setSelectedRow(row);
                        //   setDetailsOpen(true);
                        // }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <AccountBadge bankName={row.bank_name} bankId={row.bank_id} isPrimary={isPrimary} />
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.3}>
                            <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                              {formatText(row.account_number)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatText(row.account_name)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {/* <TableCell>{formatText(row.channel)}</TableCell> */}
                        <TableCell>
                          {isPrimary ? <Chip size="small" color="primary" label="Primary" /> : <Chip size="small" label="No" variant="outlined" />}
                        </TableCell>
                        <TableCell>{formatDateTime(row.created_at)}</TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button variant="outlined" size="small" onClick={() => handleEvaluate(row)}>
                            Check
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPage(0);
                setPageSize(parseInt(event.target.value, 10));
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <EmptyModuleState
              title="No saved bank accounts"
              description="The saved bank accounts endpoint returned no records for this user."
              icon="solar:wallet-bold"
            />
          </Box>
        )}
      </Card>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h6">Saved Bank Account</Typography>
              <Typography variant="body2" color="text.secondary">
                Detailed audit view for the selected account.
              </Typography>
            </Box>
            <IconButton onClick={() => setDetailsOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {selectedRow ? (
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <AccountBadge
                    bankName={selectedRow.bank_name}
                    bankId={selectedRow.bank_id}
                    isPrimary={Number(selectedRow.is_primary) === 1}
                  />
                  <Stack spacing={1} alignItems="flex-end">
                    <Label color={selectedRow.status === 'active' ? 'success' : 'default'}>
                      {formatText(selectedRow.status)}
                    </Label>
                    <Typography variant="caption" color="text.secondary">
                      Record ID: {formatText(selectedRow.id)}
                    </Typography>
                  </Stack>
                </Stack>
              </Card>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Account Identity
                    </Typography>
                    <Stack spacing={2}>
                      <DetailField label="Account name" value={formatText(selectedRow.account_name)} />
                      <DetailField label="Account number" value={formatText(selectedRow.account_number)} monospace />
                      <DetailField label="Bank name" value={formatText(selectedRow.bank_name)} />
                      <DetailField label="Bank ID" value={formatText(selectedRow.bank_id)} monospace />
                      <DetailField label="Channel" value={formatText(selectedRow.channel)} />
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Audit Signals
                    </Typography>
                    <Stack spacing={2}>
                      <DetailField label="Primary account" value={Number(selectedRow.is_primary) === 1 ? 'Yes' : 'No'} />
                      <DetailField label="Status" value={formatText(selectedRow.status)} />
                      <DetailField label="Attempt count" value={formatText(selectedRow.attempt_count)} />
                      <DetailField label="Last attempted at" value={formatDateTime(selectedRow.last_attempted_at)} />
                      <DetailField label="Last reference" value={formatText(selectedRow.last_reference)} monospace />
                    </Stack>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Timestamps
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <DetailField label="Created at" value={formatDateTime(selectedRow.created_at)} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField label="Updated at" value={formatDateTime(selectedRow.updated_at)} />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DetailField label="User ID" value={formatText(selectedRow.user_id)} monospace />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>

      <ReviewResultDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        loading={reviewLoading}
        data={reviewData}
        evaluationError={reviewError}
      />
    </Stack>
  );
}

SavedBankAccount.propTypes = {
  userId: PropTypes.string,
};